import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * ReceiptPreview — renders order data in VasisPrinter thermal-receipt style.
 * Mirrors the CSS from PekingTemplate.html (EB Garamond + Noto Serif SC,
 * double-rule dividers, tracked-caps header, etc.).
 *
 * Props:
 *   orderData   – simplified order object (line_items, subtotal_price, etc.)
 *   restaurantName – string (e.g. "Peking Chinese Restaurant")
 *   receiptUrl – original printed receipt PDF URL, when available
 */

const ReceiptWrapper = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;500;600&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

  font-family: 'EB Garamond', 'Times New Roman', Georgia, serif;
  font-size: 13px;
  line-height: 1.5;
  color: #000;
  width: 280px;
  background: #fff;
  padding: 12px 10px 18px 10px;
  border: 1px solid #bbb;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4px;
  font-size: 10px;
`;

const RestaurantName = styled.div`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  margin: 4px 0;
  padding-left: 0.25em;
`;

const OrderType = styled.div`
  text-align: center;
  margin: 14px 0 6px 0;
  font-size: 22px;
  font-weight: 500;
  letter-spacing: 0.04em;
`;

const CustomerName = styled.div`
  text-align: center;
  font-size: 23px;
  font-weight: 500;
  letter-spacing: 0.04em;
`;

const OrderMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 12px;
  margin: 8px 2px 4px 2px;
  font-style: italic;
`;

const OrderNumber = styled.strong`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.12em;
  font-style: normal;
`;

const DoubleRule = styled.hr`
  border: 0;
  border-top: 3px double #000;
  margin: 10px 0;
`;

const DashedRule = styled.div`
  border-top: 1px dashed #000;
  margin: 0;
  padding-top: 8px;
`;

const ItemRow = styled.div`
  margin-bottom: 6px;
`;

const ItemEnglish = styled.div`
  font-size: 14px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.3;
`;

const ItemChinese = styled.div`
  font-family: 'Noto Serif SC', 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-weight: 500;
  font-size: 16px;
  margin: 0;
`;

const ItemPrice = styled.span`
  font-size: 12px;
  font-weight: 400;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  float: right;
`;

const ModifierBlock = styled.div`
  font-size: 12px;
  margin-top: 3px;
  padding-left: 12px;
  line-height: 1.55;
`;

const ChildChinese = styled.div`
  font-family: 'Noto Serif SC', 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  font-size: 16px;
  padding-left: 8px;
`;

const TotalsTable = styled.div`
  margin-top: 14px;
  border-top: 3px double #000;
  border-bottom: 3px double #000;
  padding: 6px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 3px 6px;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const GrandTotal = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 6px 3px 6px;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.22em;

  .amount {
    font-size: 24px;
    font-weight: 500;
  }
`;

const Footer = styled.p`
  text-align: center;
  font-style: italic;
  font-size: 14px;
  letter-spacing: 0.18em;
  padding-left: 0.18em;
  margin: 16px 0 6px 0;
`;

const NoteSection = styled.div`
  margin: 0;
`;

const NoteLabel = styled.h4`
  font-size: 9px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-weight: 500;
  margin: 8px 0 2px 0;
  padding-left: 0.25em;
`;

const NoteText = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 6px 0;
  line-height: 1.35;
`;

const PdfCanvas = styled.canvas`
  display: block;
  width: min(280px, 100%);
  height: auto;
  background: #fff;
  border: 1px solid #bbb;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
`;

const ReceiptFallbackNotice = styled.div`
  width: 280px;
  margin-bottom: 6px;
  color: #8a5a00;
  font-size: 11px;
  line-height: 1.35;
`;

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
const RECEIPT_PDF_PROXY_BASE = 'https://api.pacificpearltraining.site/receiptPdfProxy';

let pdfJsLoadPromise = null;

function loadPdfJs() {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
    return Promise.resolve(window.pdfjsLib);
  }

  if (!pdfJsLoadPromise) {
    pdfJsLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${PDFJS_CDN}"]`);
      if (existing) {
        existing.addEventListener('load', () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
          resolve(window.pdfjsLib);
        }, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = PDFJS_CDN;
      script.async = true;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
        resolve(window.pdfjsLib);
      };
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return pdfJsLoadPromise;
}

function getProxiedReceiptUrl(receiptUrl) {
  return `${RECEIPT_PDF_PROXY_BASE}?url=${encodeURIComponent(receiptUrl)}`;
}

function rowHasInk(data, width, y) {
  let inkPixels = 0;
  const rowStart = y * width * 4;
  const minInkPixels = Math.max(3, Math.floor(width * 0.004));

  for (let x = 0; x < width; x += 1) {
    const offset = rowStart + x * 4;
    const alpha = data[offset + 3];
    const red = data[offset];
    const green = data[offset + 1];
    const blue = data[offset + 2];

    if (alpha > 8 && (red < 245 || green < 245 || blue < 245)) {
      inkPixels += 1;
      if (inkPixels >= minInkPixels) return true;
    }
  }

  return false;
}

function findReceiptContentBottom(canvas) {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const { width, height } = canvas;
  const imageData = context.getImageData(0, 0, width, height);

  for (let y = height - 1; y >= 0; y -= 1) {
    if (rowHasInk(imageData.data, width, y)) {
      return y;
    }
  }

  return height - 1;
}

function paintCroppedReceiptCanvas(sourceCanvas, targetCanvas) {
  const cropPadding = 28;
  const bottom = findReceiptContentBottom(sourceCanvas);
  const croppedHeight = Math.min(sourceCanvas.height, bottom + cropPadding);
  const targetContext = targetCanvas.getContext('2d');

  targetCanvas.width = sourceCanvas.width;
  targetCanvas.height = croppedHeight;
  targetContext.fillStyle = '#fff';
  targetContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);
  targetContext.drawImage(
    sourceCanvas,
    0,
    0,
    sourceCanvas.width,
    croppedHeight,
    0,
    0,
    targetCanvas.width,
    croppedHeight
  );
}

function PdfReceiptImage({ receiptUrl, onRenderState }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function renderPdf() {
      if (!receiptUrl || !canvasRef.current) return;

      onRenderState?.('loading');
      try {
        const pdfjsLib = await loadPdfJs();
        const pdf = await pdfjsLib.getDocument({
          url: getProxiedReceiptUrl(receiptUrl),
          withCredentials: false,
        }).promise;
        const page = await pdf.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        const targetWidth = 560;
        const scale = targetWidth / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const renderCanvas = document.createElement('canvas');
        const context = renderCanvas.getContext('2d', { willReadFrequently: true });

        renderCanvas.width = Math.ceil(viewport.width);
        renderCanvas.height = Math.ceil(viewport.height);
        context.fillStyle = '#fff';
        context.fillRect(0, 0, renderCanvas.width, renderCanvas.height);
        await page.render({ canvasContext: context, viewport }).promise;

        if (cancelled || !canvasRef.current) return;
        paintCroppedReceiptCanvas(renderCanvas, canvasRef.current);

        if (!cancelled) onRenderState?.('rendered');
      } catch (error) {
        console.error('ReceiptPreview - failed to render original receipt PDF:', error);
        if (!cancelled) onRenderState?.('failed');
      }
    }

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [receiptUrl, onRenderState]);

  return <PdfCanvas ref={canvasRef} />;
}

function ReceiptPreview({ orderData, restaurantName, receiptUrl }) {
  const [pdfRenderState, setPdfRenderState] = useState(receiptUrl ? 'loading' : 'missing');

  useEffect(() => {
    setPdfRenderState(receiptUrl ? 'loading' : 'missing');
  }, [receiptUrl]);

  if (receiptUrl && pdfRenderState !== 'failed') {
    return <PdfReceiptImage receiptUrl={receiptUrl} onRenderState={setPdfRenderState} />;
  }

  if (!orderData || !orderData.line_items) return null;

  const phone = orderData.shipping_address?.last_name || '';
  const dateStr = orderData.created_at
    ? new Date(orderData.created_at).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
    : '';

  const lineItems = orderData.line_items;
  const subtotal = orderData.subtotal_price;
  const tax = orderData.total_tax;
  const total = orderData.total_price;

  // Group items: parent + children (children have _io_parent_order_group)
  const renderItems = () => {
    const rendered = [];
    let i = 0;
    while (i < lineItems.length) {
      const item = lineItems[i];
      const isChild = item.properties?.some(p => p.name === '_io_parent_order_group');

      if (isChild) {
        // Standalone child (shouldn't happen, but handle gracefully)
        i++;
        continue;
      }

      // Collect children
      const children = [];
      let j = i + 1;
      while (j < lineItems.length && lineItems[j].properties?.some(p => p.name === '_io_parent_order_group')) {
        children.push(lineItems[j]);
        j++;
      }

      rendered.push(
        <ItemRow key={i}>
          {i > 0 && <DashedRule />}
          <div style={{ overflow: 'hidden' }}>
            <ItemPrice>${(parseFloat(item.price) * item.quantity).toFixed(2)}</ItemPrice>
            <ItemEnglish>
              {item.quantity > 1 ? `${item.quantity}X ` : ''}
              {renderVariantPrefix(item)}
              {item.title}
              {renderVariantSuffix(item)}
            </ItemEnglish>
          </div>
          {item.sku && (
            <ItemChinese>
              {item.quantity > 1 ? `【${item.quantity}份】` : ''}{item.sku}
            </ItemChinese>
          )}
          {renderModifiers(item, children)}
          {children.map((child, ci) => (
            <div key={`child-${ci}`} style={{ overflow: 'hidden' }}>
              {child.sku && (
                <ChildChinese>【{child.sku}】</ChildChinese>
              )}
              {parseFloat(child.price) > 0 && (
                <ItemPrice style={{ paddingLeft: '8px' }}>
                  ${(parseFloat(child.price) * child.quantity).toFixed(2)}
                </ItemPrice>
              )}
            </div>
          ))}
        </ItemRow>
      );

      i = j;
    }
    return rendered;
  };

  const renderVariantPrefix = (item) => {
    if (!item.variant_title) return '';
    const parts = item.variant_title.split(' / ');
    const size = parts[0];
    if (size && size !== '#' && !size.includes('W.')) {
      return <strong>({size}) </strong>;
    }
    return '';
  };

  const renderVariantSuffix = (item) => {
    if (!item.variant_title) return '';
    const parts = item.variant_title.split(' / ');
    const size = parts[0];
    const option = parts[1];

    const suffix = [];
    if (size && size !== '#' && size.includes('W.')) {
      suffix.push(<strong key="size"> ({size})</strong>);
    }
    if (option && option !== '#' && option !== size) {
      suffix.push(<span key="option"> ({option})</span>);
    }
    return suffix;
  };

  const renderModifiers = (item, children) => {
    const props = item.properties || [];
    const covered = new Set();

    const modElements = [];
    props.forEach((p, pi) => {
      if (p.name.includes('_io_')) return;
      if (!p.value || p.value === 'None' || p.value === 'No' || p.value === 'Yes') return;
      if (p.name === 'Special Requests' || p.name === 'size(id:576434)' || p.name === ':') return;
      if (p.value.includes('LESS') || p.value.includes('ON THE SIDE') || p.value.includes('WITH') || p.value.includes('ONLY')) return;
      if (p.value.includes('ADD') || p.value.includes('SPECIAL INSTRUCTIONS')) return;

      if (p.name.includes('Chinese') || p.name.includes('中文')) {
        modElements.push(
          <div key={`mod-${pi}`} style={{
            fontFamily: "'Noto Serif SC', 'Noto Sans SC', sans-serif",
            fontWeight: 400,
            fontSize: '16px',
          }}>【{p.value}】</div>
        );
      } else if (p.name.includes('Side Option')) {
        modElements.push(<div key={`mod-${pi}`}>{p.value};</div>);
        const key = p.value.split(' (+$')[0].split(' +$')[0].trim();
        covered.add(key);
      } else {
        modElements.push(<div key={`mod-${pi}`}><strong>{p.value};</strong></div>);
        const key = p.value.split(' (+$')[0].split(' +$')[0].trim();
        covered.add(key);
      }
    });

    // Augment with child English titles not already covered
    children.forEach((child, ci) => {
      let childEn = child.variant_title || child.title;
      if (childEn && childEn.includes('Special Instruction')) childEn = '';
      if (childEn) {
        const key = childEn.split(' (+$')[0].split(' +$')[0].trim();
        if (!covered.has(key)) {
          modElements.push(<div key={`child-en-${ci}`}><strong>{childEn};</strong></div>);
          covered.add(key);
        }
      }
    });

    if (modElements.length === 0) return null;
    return <ModifierBlock>{modElements}</ModifierBlock>;
  };

  return (
    <>
      {receiptUrl && pdfRenderState === 'failed' && (
        <ReceiptFallbackNotice>
          Original receipt PDF could not be rendered. Showing generated fallback.
        </ReceiptFallbackNotice>
      )}
      <ReceiptWrapper>
        <Header>
          <RestaurantName>{restaurantName || 'Restaurant'}</RestaurantName>
        </Header>

        <OrderType>来取 Pick Up</OrderType>
        <CustomerName>{phone}</CustomerName>

        <OrderMeta>
          <OrderNumber>{orderData.name || ''}</OrderNumber>
          <span>{dateStr}</span>
        </OrderMeta>

        {orderData.note && (
          <NoteSection>
            <NoteLabel>重要备注 Note</NoteLabel>
            <NoteText>{orderData.note}</NoteText>
          </NoteSection>
        )}

        <DoubleRule />

        {renderItems()}

        <TotalsTable>
          {subtotal != null && (
            <TotalRow>
              <span>Subtotal price:</span>
              <span>${parseFloat(subtotal).toFixed(2)}</span>
            </TotalRow>
          )}
          {tax != null && parseFloat(tax) > 0 && (
            <TotalRow>
              <span>Total tax:</span>
              <span>${parseFloat(tax).toFixed(2)}</span>
            </TotalRow>
          )}
          {total != null && (
            <GrandTotal>
              <strong>Total price:</strong>
              <span className="amount">${parseFloat(total).toFixed(2)}</span>
            </GrandTotal>
          )}
        </TotalsTable>

        <Footer>Thank You Very Much!</Footer>
      </ReceiptWrapper>
    </>
  );
}

export default ReceiptPreview;
